import { ReactNode, useState, useCallback, useRef } from 'react';
import { DashboardContext, DashboardFilters, DashboardContextValue } from './DashboardContext';
import { useRole } from '@/shared/contexts/RoleContext';

export function DashboardProvider({ children }: { children: ReactNode }) {
  const { role } = useRole();
  const [filters, setFiltersState] = useState<DashboardFilters>({});
  const [isRefreshing, setIsRefreshing] = useState(false);
  
  const refreshFns = useRef<Map<string, () => Promise<void> | void>>(new Map());

  const setFilters = useCallback((newFilters: Partial<DashboardFilters>) => {
    setFiltersState(prev => ({ ...prev, ...newFilters }));
  }, []);

  const registerWidgetRefresh = useCallback((id: string, fn: () => void) => {
    refreshFns.current.set(id, fn);
  }, []);

  const unregisterWidgetRefresh = useCallback((id: string) => {
    refreshFns.current.delete(id);
  }, []);

  const refreshAll = useCallback(async () => {
    setIsRefreshing(true);
    const promises = Array.from(refreshFns.current.values()).map(fn => Promise.resolve(fn()));
    await Promise.allSettled(promises);
    setIsRefreshing(false);
  }, []);

  const value: DashboardContextValue = {
    role,
    filters,
    setFilters,
    isRefreshing,
    refreshAll,
    registerWidgetRefresh,
    unregisterWidgetRefresh,
  };

  return <DashboardContext.Provider value={value}>{children}</DashboardContext.Provider>;
}
